// src/Pages/Home/TrendFashion/sections/TopPicksSliderSection.tsx
import React, { useMemo } from "react";
import { Card, Col, Container, Image, Row, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Autoplay, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import Countdown from "react-countdown";

import type { StoreSection } from "../../../../redux/services/store/storePagesApi";
import { useListStoreProductsQuery } from "../../../../redux/services/store/storeProductsApi";

import "swiper/css";

type Props = { section: StoreSection };

type ProductItem = {
  id: number;
  slug: string;
  name: string;
  image?: { cdn_url?: string | null; alt_text?: string | null } | null;
  price_from?: string | number | null;
  compare_at_from?: string | number | null;
  currency_code?: string | null;
};

const safeJson = <T,>(v: any, fallback: T): T => {
  try {
    if (!v) return fallback;
    if (typeof v === "string") return JSON.parse(v) as T;
    return (v as T) ?? fallback;
  } catch {
    return fallback;
  }
};

const toNum = (v: any): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const EMPTY_IMG =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";

const formatPrice = (currency: string, value: any) => {
  const num = toNum(value);
  return `${currency} ${num.toFixed(2)}`;
};

const parseCountdownDate = (value: any): number | null => {
  if (!value) return null;
  const time = new Date(String(value)).getTime();
  return Number.isFinite(time) && time > 0 ? time : null;
};

const normalizeSort = (value: any) => {
  const token = String(value ?? "").trim().toLowerCase();
  if (token === "price_asc" || token === "price_desc" || token === "popular" || token === "newest") {
    return token;
  }
  return "newest";
};

const TopPicksSliderSection: React.FC<Props> = ({ section }) => {
  const settings = useMemo(() => safeJson<any>(section.settings_json, {}), [section.settings_json]);
  const ds = useMemo(() => safeJson<any>(section.data_source_json, {}), [section.data_source_json]);
  const header = useMemo(() => settings?.header || {}, [settings]);
  const countdownSettings = useMemo(() => settings?.countdown || {}, [settings]);

  const limit = Number(ds.limit ?? 12);
  const sort = normalizeSort(ds.sort);
  const categoryId = Number(ds.category_id ?? ds.categoryId ?? 0);
  const categorySlug = String(ds.category_slug ?? ds.categorySlug ?? "").trim();
  const hasCategorySource = categoryId > 0 || !!categorySlug;
  const countdownDate = useMemo(
    () =>
      parseCountdownDate(
        countdownSettings?.end_at ||
          countdownSettings?.expires_at ||
          countdownSettings?.expiry_at ||
          countdownSettings?.target_date
      ),
    [countdownSettings]
  );

  const { data, isLoading, isError } = useListStoreProductsQuery(
    {
      skip: 0,
      limit,
      category_id: categoryId > 0 ? categoryId : null,
      category_slug: categorySlug || null,
      sort,
    } as any,
    { skip: !hasCategorySource }
  );

  const items: ProductItem[] = useMemo(() => {
    const list = (data as any)?.items ?? [];
    return (Array.isArray(list) ? list : []).map((p: any) => ({
      id: p.id,
      slug: p.slug,
      name: p.name,
      image: p.image ?? null,
      price_from: p.price_from ?? null,
      compare_at_from: p.compare_at_from ?? null,
      currency_code: p.currency_code ?? "INR",
    }));
  }, [data]);

  if (!section?.is_active) return null;

  const titleHtml =
    header?.title_html ||
    (section.section_type === "todays_trend_fashion"
      ? `<span>Today's</span> <span class="fw-semibold">Trend</span>`
      : `<span>Top Picks On</span> <span class="fw-semibold"> Fashion</span>`);
  const kicker = String(header?.kicker ?? "").trim();
  const descriptionHtml = header?.description_html || header?.description || "";

  const sliderClass = settings?.styles?.slider_class || "top-picks-bootstrap-slider";
  const sectionClass = settings?.styles?.section_class || "section";
  const containerClass = settings?.styles?.container_class || "container-custom";

  const loop = settings?.slider?.loop ?? items.length > 4;
  const spaceBetweenDesktop = Number(settings?.slider?.space_between ?? 24);
  const autoplayDelay = Number(settings?.slider?.autoplay_delay ?? 3200);
  const slidesPerViewDesktop = Number(settings?.slider?.slides_per_view ?? 4);
  const countdownLabels = {
    days: String(countdownSettings?.labels?.days || "Days"),
    hours: String(countdownSettings?.labels?.hours || "Hours"),
    minutes: String(countdownSettings?.labels?.minutes || "Minutes"),
    seconds: String(countdownSettings?.labels?.seconds || "Seconds"),
  };
  const countdownCompleteText = String(countdownSettings?.complete_text || "Offer ended");
  const countdownEnabled = countdownSettings?.enabled !== false && !!countdownDate;

  return (
    <>
      <style>
        {`
          .top-picks-kicker {
            display: inline-block;
            margin-bottom: 10px;
            font-size: 0.9rem;
            font-weight: 700;
            letter-spacing: 0.16em;
            text-transform: uppercase;
            color: #9a3412;
          }

          .top-picks-section-header .title {
            margin-bottom: 0;
          }

          .top-picks-header-description {
            max-width: 760px;
            margin: 16px auto 0;
            color: #6b7280;
            line-height: 1.75;
          }

          .top-picks-countdown {
            display: flex;
            align-items: stretch;
            justify-content: center;
            flex-wrap: wrap;
            gap: 12px;
            margin-top: 26px;
          }

          .top-picks-countdown-item {
            min-width: 88px;
            padding: 14px 12px;
            border-radius: 18px;
            background: #fff;
            border: 1px solid rgba(17, 24, 39, 0.08);
            box-shadow: 0 10px 24px rgba(15, 23, 42, 0.07);
          }

          .top-picks-countdown-value {
            display: block;
            font-size: 1.65rem;
            font-weight: 700;
            line-height: 1;
            color: #111827;
          }

          .top-picks-countdown-label {
            display: block;
            margin-top: 6px;
            font-size: 0.72rem;
            font-weight: 700;
            letter-spacing: 0.14em;
            text-transform: uppercase;
            color: #6b7280;
          }

          .top-picks-countdown-ended {
            margin-top: 18px;
            color: #6b7280;
            font-weight: 600;
          }

          .top-picks-bootstrap-slider {
            padding: 6px 2px 10px;
          }

          .top-picks-bootstrap-slider .swiper-button-prev,
          .top-picks-bootstrap-slider .swiper-button-next {
            width: 42px;
            height: 42px;
            border-radius: 50%;
            background: #fff;
            color: #111;
            box-shadow: 0 10px 28px rgba(0,0,0,0.12);
          }

          .top-picks-bootstrap-slider .swiper-button-prev:after,
          .top-picks-bootstrap-slider .swiper-button-next:after {
            font-size: 14px;
            font-weight: 800;
          }

          .top-picks-product-card {
            border: 1px solid rgba(0,0,0,0.06);
            border-radius: 22px;
            overflow: hidden;
            background: #fff;
            transition: transform 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease;
            box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
          }

          .top-picks-product-card:hover {
            transform: translateY(-6px);
            box-shadow: 0 16px 36px rgba(15, 23, 42, 0.12);
            border-color: rgba(0,0,0,0.08);
          }

          .top-picks-image-wrap {
            position: relative;
            overflow: hidden;
            background: #f8f9fa;
            aspect-ratio: 3 / 4;
          }

          .top-picks-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: top center;
            transition: transform 0.5s ease;
          }

          .top-picks-product-card:hover .top-picks-image {
            transform: scale(1.06);
          }

          .top-picks-top-actions {
            position: absolute;
            top: 14px;
            right: 14px;
            z-index: 3;
            display: flex;
            flex-direction: column;
            gap: 10px;
          }

          .top-picks-icon-btn {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: rgba(255,255,255,0.96);
            color: #111;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            text-decoration: none;
            box-shadow: 0 8px 18px rgba(0,0,0,0.12);
            transition: transform 0.25s ease, background 0.25s ease;
          }

          .top-picks-icon-btn:hover {
            transform: translateY(-2px);
            background: #fff;
            color: #111;
          }

          .top-picks-discount-badge {
            position: absolute;
            top: 14px;
            left: 14px;
            z-index: 3;
            background: #111;
            color: #fff;
            border-radius: 999px;
            padding: 7px 12px;
            font-size: 12px;
            font-weight: 700;
            line-height: 1;
            letter-spacing: 0.3px;
          }

          .top-picks-card-body {
            padding: 18px 18px 18px;
          }

          .top-picks-product-title {
            color: #111827;
            text-decoration: none;
            font-size: 16px;
            font-weight: 600;
            line-height: 1.35;
            min-height: 43px;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            margin-bottom: 0;
          }

          .top-picks-product-title:hover {
            color: #111827;
          }

          .top-picks-price-row {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 10px;
            margin-top: 10px;
            margin-bottom: 8px;
          }

          .top-picks-price {
            font-size: 18px;
            font-weight: 700;
            color: #111;
            line-height: 1;
          }

          .top-picks-old-price {
            font-size: 14px;
            font-weight: 500;
            color: #9ca3af;
            text-decoration: line-through;
            line-height: 1;
          }

          .top-picks-bottom-row {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            gap: 10px;
            margin-top: 0;
          }

          .top-picks-shop-btn {
            border-radius: 999px;
            padding: 10px 16px;
            font-size: 14px;
            font-weight: 600;
            line-height: 1;
          }

          @media (max-width: 575.98px) {
            .top-picks-countdown {
              gap: 10px;
            }

            .top-picks-countdown-item {
              min-width: 72px;
              padding: 12px 10px;
            }

            .top-picks-countdown-value {
              font-size: 1.35rem;
            }

            .top-picks-card-body {
              padding: 16px;
            }

            .top-picks-price {
              font-size: 17px;
            }

            .top-picks-product-title {
              font-size: 15px;
            }

            .top-picks-bottom-row {
              flex-direction: column;
              align-items: stretch;
            }
          }
        `}
      </style>

      {/*
        This section is JSON-driven from store_page/store_section:
        - data_source_json.category_id/category_slug controls product source
        - settings_json.countdown.end_at enables the expiry counter
      */}
      <section className={sectionClass}>
        <Container fluid className={containerClass}>
          <Row className="justify-content-center align-items-center mb-4">
            <Col lg={7}>
              <div className="top-picks-section-header text-center mb-4 mb-lg-5">
                {kicker ? <p className="top-picks-kicker mb-0">{kicker}</p> : null}
                <h2 className="title fw-normal" dangerouslySetInnerHTML={{ __html: titleHtml }} />
                {descriptionHtml ? (
                  <div
                    className="top-picks-header-description"
                    dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                  />
                ) : null}

                {countdownEnabled ? (
                  <Countdown
                    date={countdownDate as number}
                    renderer={({ days, hours, minutes, seconds, completed }) =>
                      completed ? (
                        <div className="top-picks-countdown-ended">{countdownCompleteText}</div>
                      ) : (
                        <div className="top-picks-countdown">
                          <div className="top-picks-countdown-item">
                            <span className="top-picks-countdown-value">{days}</span>
                            <span className="top-picks-countdown-label">{countdownLabels.days}</span>
                          </div>
                          <div className="top-picks-countdown-item">
                            <span className="top-picks-countdown-value">{hours}</span>
                            <span className="top-picks-countdown-label">{countdownLabels.hours}</span>
                          </div>
                          <div className="top-picks-countdown-item">
                            <span className="top-picks-countdown-value">{minutes}</span>
                            <span className="top-picks-countdown-label">{countdownLabels.minutes}</span>
                          </div>
                          <div className="top-picks-countdown-item">
                            <span className="top-picks-countdown-value">{seconds}</span>
                            <span className="top-picks-countdown-label">{countdownLabels.seconds}</span>
                          </div>
                        </div>
                      )
                    }
                  />
                ) : null}
              </div>
            </Col>
          </Row>

          {isLoading ? (
            <div className="py-5 text-center">
              <Spinner animation="border" role="status" />
            </div>
          ) : isError ? (
            <div className="text-center text-danger">Failed to load products</div>
          ) : !hasCategorySource ? (
            <div className="text-center text-muted">
              Configure `data_source_json.category_id` or `data_source_json.category_slug` for this section.
            </div>
          ) : !items.length ? (
            <div className="text-center text-muted">No products found.</div>
          ) : (
            <Row>
              <Col lg={12}>
                <div className="position-relative">
                  <Swiper
                    className={sliderClass}
                    modules={[Navigation, Autoplay]}
                    navigation={{
                      nextEl: ".top-picks-swiper-next",
                      prevEl: ".top-picks-swiper-prev",
                    }}
                    observer
                    observeParents
                    slidesPerView={1}
                    spaceBetween={16}
                    breakpoints={{
                      0: { slidesPerView: 1, spaceBetween: 16 },
                      576: { slidesPerView: 2, spaceBetween: 18 },
                      768: { slidesPerView: 2, spaceBetween: 20 },
                      992: { slidesPerView: 3, spaceBetween: 22 },
                      1200: {
                        slidesPerView: slidesPerViewDesktop,
                        spaceBetween: spaceBetweenDesktop,
                      },
                    }}
                    loop={!!loop}
                    autoplay={{ delay: autoplayDelay, disableOnInteraction: false }}
                  >
                    <div
                      className="top-picks-swiper-prev swiper-button-prev h-auto"
                      tabIndex={0}
                      role="button"
                      aria-label="Previous slide"
                    />
                    <div
                      className="top-picks-swiper-next swiper-button-next h-auto"
                      tabIndex={0}
                      role="button"
                      aria-label="Next slide"
                    />

                    {items.slice(0, limit).map((p) => {
                      const img = p.image?.cdn_url || "";
                      const alt = p.image?.alt_text || p.name || "";
                      const currency = p.currency_code || "INR";
                      const currentPrice = toNum(p.price_from);
                      const comparePrice = toNum(p.compare_at_from);
                      const hasCompare = comparePrice > currentPrice && comparePrice > 0;
                      const discount =
                        hasCompare && comparePrice > 0
                          ? Math.round(((comparePrice - currentPrice) / comparePrice) * 100)
                          : 0;

                      const productUrl = `/product/${p.slug}`;

                      return (
                        <SwiperSlide key={p.id}>
                          <Card className="top-picks-product-card h-100 border-0">
                            <div className="top-picks-image-wrap">
                              {discount > 0 ? (
                                <span className="top-picks-discount-badge">{discount}% OFF</span>
                              ) : null}

                              <div className="top-picks-top-actions">
                                <Link to={productUrl} className="top-picks-icon-btn" aria-label="View product">
                                  <i className="bi bi-eye" />
                                </Link>
                                <Link to={productUrl} className="top-picks-icon-btn" aria-label="Shop now">
                                  <i className="bi bi-bag" />
                                </Link>
                              </div>

                              <Link to={productUrl} className="d-block w-100 h-100 text-decoration-none">
                                <Image
                                  src={img || EMPTY_IMG}
                                  alt={alt}
                                  className="top-picks-image d-block"
                                />
                              </Link>
                            </div>

                            <Card.Body className="top-picks-card-body d-flex flex-column">
                              <Link to={productUrl} className="top-picks-product-title">
                                {p.name}
                              </Link>

                              <div className="top-picks-price-row">
                                <span className="top-picks-price">
                                  {formatPrice(currency, p.price_from)}
                                </span>

                                {hasCompare ? (
                                  <span className="top-picks-old-price">
                                    {formatPrice(currency, p.compare_at_from)}
                                  </span>
                                ) : null}
                              </div>

                              <div className="top-picks-bottom-row">
                                <Link to={productUrl} className="btn btn-dark top-picks-shop-btn">
                                  Shop Now
                                </Link>
                              </div>
                            </Card.Body>
                          </Card>
                        </SwiperSlide>
                      );
                    })}
                  </Swiper>
                </div>
              </Col>
            </Row>
          )}
        </Container>
      </section>
    </>
  );
};

export default TopPicksSliderSection;
